/**
 * Image positioning algorithm for scattered Spark Card images
 *
 * Rules from Figma design:
 * - Up to 4 images displayed in a "polaroid scatter" effect
 * - Images positioned at 4 corner zones with inward shifts
 * - Rotation applied: alternating -6° and +6° (clockwise)
 * - Image sizing based on aspect ratio
 */

export interface ImageDimensions {
  width: number | null; // null means auto (preserve aspect ratio)
  height: number | null; // null means auto (preserve aspect ratio)
}

export interface ImagePosition {
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
  zIndex: number;
  isLeftSide: boolean; // Whether image is positioned on left side of card
}

export type AspectRatioType = 'square' | 'portrait' | 'landscape';

/**
 * Determine image aspect ratio type
 */
export const getAspectRatioType = (width: number, height: number): AspectRatioType => {
  const ratio = width / height;
  if (ratio > 1.1) return 'landscape';
  if (ratio < 0.9) return 'portrait';
  return 'square';
};

/**
 * Get image dimensions based on aspect ratio
 * - Square (1:1): 76×76px (fixed)
 * - Portrait (vertical): height=110px, width=auto (preserve aspect ratio)
 * - Landscape (horizontal): width=110px, height=auto (preserve aspect ratio)
 */
export const getImageSize = (aspectRatioType: AspectRatioType): ImageDimensions => {
  switch (aspectRatioType) {
    case 'square':
      return { width: 76, height: 76 };
    case 'portrait':
      return { width: null, height: 110 }; // width auto-calculated
    case 'landscape':
      return { width: 110, height: null }; // height auto-calculated
    default:
      return { width: 76, height: 76 };
  }
};

/**
 * Calculate rotation for image at given index
 * Default pattern for 4 images: -6°, +6°, -6°, +6°
 *
 * For specific image counts, use getRotationForCount() instead.
 */
export const getRotation = (index: number): number => {
  const rotations = [6, -6, -6, 6];
  return rotations[index % 4];
};

/**
 * Get rotation based on image count and index
 * State 1 (1 image): +6°
 * State 2 (2 images): Left +6°, Right -6°
 * State 3 (3 images): First -6°, Second -6°, Third +6°
 * State 4 (4 images): -6°, +6°, -6°, +6°
 */
export const getRotationForCount = (index: number, totalImages: number): number => {
  if (totalImages === 1) {
    return 6; // Single image: +6°
  }
  if (totalImages === 2) {
    return index === 0 ? -6 : 6; // Left: -6°, Right: +6°
  }
  if (totalImages === 3) {
    // First: +6°, Second: +6°, Third: -6°
    return index === 2 ? -6 : 6;
  }
  // 4 images: default pattern
  return getRotation(index);
};

/**
 * Get inward shift amount based on aspect ratio
 * - Vertical/square images: 20px
 * - Horizontal images: 30px
 */
export const getShiftAmount = (aspectRatioType: AspectRatioType): number => {
  return aspectRatioType === 'landscape' ? 30 : 20;
};

/**
 * Placement Zone Definitions:
 *
 * The card is divided vertically into 3 equal sections using 2 horizontal guide lines:
 * - Upper guide line at 33.33% from top
 * - Lower guide line at 66.67% from top
 *
 * Four placement points are at the intersections of card edges and guide lines:
 * - Point 0: Left edge at upper guide (33.33%)
 * - Point 1: Right edge at upper guide (33.33%)
 * - Point 2: Left edge at lower guide (66.67%)
 * - Point 3: Right edge at lower guide (66.67%)
 *
 * Images are centered on their placement point before shifts/rotations.
 */
const PLACEMENT_POINTS = {
  UPPER_GUIDE: '33.33%', // 1/3 from top
  CENTER: '50%', // Center of card
  LOWER_GUIDE: '66.67%', // 2/3 from top
};

/**
 * Calculate position for a single image based on:
 * - Total number of images (1-4)
 * - Image index (0-3)
 * - Image aspect ratio
 *
 * Step 1: Images are centered on placement points at card edges.
 * Step 2: Images are shifted inward toward card center (20px for vertical/square, 30px for horizontal).
 */
export const calculateImagePosition = (
  index: number,
  totalImages: number,
  aspectRatioType: AspectRatioType = 'square'
): ImagePosition => {
  const size = getImageSize(aspectRatioType);

  // Calculate offset to center image on the edge (half the image extends outside)
  // Use width if available, otherwise use height, with 76 as fallback
  const refDimension = size.width ?? size.height ?? 76;
  const centerOffset = -refDimension / 2; // Center image on the edge (no inward shift - that animates)

  // Special handling for different image counts
  if (totalImages === 1) {
    // State 1: Single image on left side, centered vertically
    return {
      top: PLACEMENT_POINTS.CENTER,
      left: `${centerOffset}px`,
      zIndex: 1,
      isLeftSide: true,
    };
  }

  if (totalImages === 2) {
    // State 2: Both images centered vertically
    // Left image at center, Right image at center
    if (index === 0) {
      return {
        top: PLACEMENT_POINTS.CENTER,
        left: `${centerOffset}px`,
        zIndex: 1,
        isLeftSide: true,
      };
    }
    return {
      top: PLACEMENT_POINTS.CENTER,
      right: `${centerOffset}px`,
      zIndex: 2,
      isLeftSide: false,
    };
  }

  if (totalImages === 3) {
    // State 3: Right image centered, others at default guide lines
    // First (left upper), Second (right centered), Third (left lower)
    const threeImagePositions: ImagePosition[] = [
      {
        top: PLACEMENT_POINTS.UPPER_GUIDE,
        left: `${centerOffset}px`,
        zIndex: 1,
        isLeftSide: true,
      },
      {
        // Right image centered relative to card
        top: PLACEMENT_POINTS.CENTER,
        right: `${centerOffset}px`,
        zIndex: 2,
        isLeftSide: false,
      },
      {
        top: PLACEMENT_POINTS.LOWER_GUIDE,
        left: `${centerOffset}px`,
        zIndex: 3,
        isLeftSide: true,
      },
    ];
    return threeImagePositions[index];
  }

  // Four images: all four placement points
  const positions: ImagePosition[] = [
    {
      // Point 0: Left edge at upper guide
      top: PLACEMENT_POINTS.UPPER_GUIDE,
      left: `${centerOffset}px`,
      zIndex: 1,
      isLeftSide: true,
    },
    {
      // Point 1: Right edge at upper guide
      top: PLACEMENT_POINTS.UPPER_GUIDE,
      right: `${centerOffset}px`,
      zIndex: 2,
      isLeftSide: false,
    },
    {
      // Point 2: Left edge at lower guide
      top: PLACEMENT_POINTS.LOWER_GUIDE,
      left: `${centerOffset}px`,
      zIndex: 3,
      isLeftSide: true,
    },
    {
      // Point 3: Right edge at lower guide
      top: PLACEMENT_POINTS.LOWER_GUIDE,
      right: `${centerOffset}px`,
      zIndex: 4,
      isLeftSide: false,
    },
  ];

  return positions[index] || positions[0];
};

/**
 * Get preview image URL from a response card
 * Uses a generic type to avoid strict type checking on nested optional properties
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getResponseImageUrl = (responseCard: any): string | null => {
  // Get image from linkImageSets
  const linkImageUrl = responseCard?.linkImageSets?.items?.[0]?.images?.[0]?.url;
  if (linkImageUrl) return linkImageUrl;

  return null;
};
